import produce from 'immer';

const primary = '#8256f8';
const black = '#202224';
const gray = '#88949d';

const theme = {
	spacing: 2,
	pageMaxWidth: 1300,
	pageMinWidth: 320,
	containerBreakpoint: 1620,
	breakpoints: ['320px', '375px', '425px', '768px', '1024px', '1440px', '1921px'],
	fonts: {
		fontFamily: 'Averta',
		fontFamilyHeadings: 'Averta',
		fontSize: '1.6rem',
		fontSizeTablet: '1.8rem',
		fontSizeDesktop: '1.8rem',
		lineHeight: 1.8,
		lineHeightHeadingsDesktop: 1.6
	},
	colors: {
		primaryLighter3: '#e2d8fd',
		primaryLighter2: '#d1c1fc',
		primaryLighter: '#bda6fb',
		primaryLight: '#a485f9',
		primary: primary,
		primaryDark: '#744ddf',
		primaryDarker: '#6543c3',
		primaryDarker2: '#5337a0',
		primaryDarker3: '#3b2771',
		primaryDarker4: '#160C48',
		secondaryLighter3: '#d6daf3',
		secondaryLighter2: '#bfc5ec',
		secondaryLighter: '#a3abe3',
		secondaryLight: '#808cd9',
		secondary: '#4f5ecb',
		secondaryDark: '#4756b6',
		secondaryDarker: '#3d4b9f',
		secondaryDarker2: '#323d82',
		secondaryDarker3: '#232b5b',
		grayLighter3: '#dfe4e4',
		grayLighter2: '#cdd5d5',
		grayLighter: '#b9c5c4',
		grayLight: '#a2b2b1',
		gray: gray,
		grayDark: '#7a8d8b',
		grayDarker: '#6b7c7a',
		grayDarker2: '#586665',
		grayDarker3: '#3f4948',
		blackLighter: '#737676',
		black: black,
		blackDarker: '#1c1f1f',
		textColor: gray,
		primaryGradient: 'linear-gradient(270deg, #9A53DD 0%, #755AF8 39.53%, #455FC0 100%)',
		primaryGradientInverse: 'linear-gradient(270deg, #5736F3 0%, #9A53DD 100%)',
		borderColor: '#e6e6e6',
		borderDark: '#cecece',
		entryMaskColor: '#fff',
		headerTextColor: '#232323',
		headerTextHoverColor: primary,
		logoTextColor: '#393939',
		success: '#4CAE65',
		warning: '#DE8720',
		hamburgerColor: black
	},
	buttons: {
		buttonHeightSmall: '4rem',
		buttonHeight: '5rem',
		buttonHeightLarge: '6rem',
		buttonPaddingSmall: '1rem',
		buttonPadding: '2.5rem',
		buttonPaddingLarge: '4rem',
		buttonFontWeight: 600,
		buttonFontSizeSmall: '1.4rem',
		buttonFontSize: '1.4rem',
		buttonFontSizeLarge: '1.6rem',
		buttonRadius: '2px',
		headerNightButtonBackground: '#fff',
		headerNightButtonColor: black,
		headerNightButtonBorderColor: '#e6e6e6',
		headerNightButtonBackgroundHover: '#fafafa',
		headerNightButtonColorHover: black,
		headerNightButtonBorderColorHover: '#cecece',
	},
	header: {
		headerHeightMobile: '8rem',
		headerHeightDesktop: '14rem',
		headerBackground: '#fff',
	},
	footer: {
		footerHeightDesktop: '12rem'
	},
	border: '1px solid #eee',
	sizes: {
		sidebarWidth: 64
	},
	routeTransitions: {
		enterDuration: 500,
		exitDuration: 500
	},
	hero: {
		objectsFill: '#F4F6F8',
		objectsLightFill: '#FFFFFF',
		windowFill: '#F2f2f2',
		sunMoonFill: '#FFB053',
		lightsFill: '#DDE3E9',
		lightsLightFill: '#F2F2F2',
		clockFill: '#F2F2F2',
		clockFillDark: '#eee',
		objectsFillF2: '#f2f2f2',
		objectsFillC5: '#C5CFD6',
		objectsFillDD: '#DDE3E9',
		objectsFillFF: '#FFFFFF',
		objectsFillCB: '#CBCBCB',
		objectsFill00: '#000000',
		windowPanels: '#fff',
		shapeOpacity: 1,
		visibleDuringNight: 0,
		visibleDuringDay: 1,
		heroTitleColor: black,
		heroTextColor: gray
	},
	theming: {
		nightThemeGradient: 'linear-gradient(180deg, #160C48 0%, #432DB1 38.62%, #472EB3 53.16%, #FFFFFF 100%)',
		nightThemeGradientOpacity: 0,
		nightDayTransitionDuration: '300ms'
	}
};

const nightTheme = produce(theme, (draft) => {
	draft.theming.nightThemeGradientOpacity = 1;
	draft.colors.logoTextColor = '#fff';
	draft.colors.headerTextColor = '#fff';
	draft.colors.headerTextHoverColor = '#fff';
	draft.hero.objectsFill = '#1A1049';
	draft.hero.objectsFillF2 = '#1A1049';
	draft.hero.objectsFillC5 = '#1A1049';
	draft.hero.objectsFillFF = '#1A1049';
	draft.hero.objectsFillCB = '#1A1049';
	draft.hero.objectsFill00 = '#FFFFFF';
	draft.hero.clockFill = '#1A1049';
	draft.hero.clockFillDark = '#1A1049';
	draft.hero.objectsLightFill = '#341F95';
	draft.hero.objectsFillDD = '#341F95';
	draft.hero.lightsFill = '#1A1049';
	draft.hero.shapeOpacity = 0.2;
	draft.hero.windowFill = '#120e19';
	draft.hero.sunMoonFill = '#aaa';
	draft.hero.lightsLightFill = '#FFB053';
	draft.hero.heroTitleColor = '#fff';
	draft.hero.heroTextColor = '#fff';
	draft.hero.windowPanels = '#3F2E89';
	draft.hero.visibleDuringNight = 1;
	draft.hero.visibleDuringDay = 0;
	draft.buttons.headerNightButtonBackground = 'transparent';
	draft.buttons.headerNightButtonColor = '#fff';
	draft.buttons.headerNightButtonBorderColor = '#2F1E83';
	draft.buttons.headerNightButtonBackgroundHover = 'transparent';
	draft.buttons.headerNightButtonColorHover = '#fff';
	draft.buttons.headerNightButtonBorderColorHover = '#5237da';
	draft.header.headerBackground = draft.colors.primaryDarker4;
	draft.colors.hamburgerColor = '#fff';
});

export {
	theme,
	nightTheme
};

export default theme;
